
import Numpad from '@/libs/common/components/Numpad.vue';
import { Component, Watch, Ref } from 'vue-property-decorator';
import Page from '@/Page.vue';
import KeyInput from '@/libs/common/components/KeyInput.vue';
import {
    LocationModel,
    QueryOperator,
    StationModel,
    TenantDeviceSubscriptionModel,
    TenantUserModel,
    WorkplaceModel,
} from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import getBrowserFingerprint from 'get-browser-fingerprint';
import { tenantDeviceSubscriptionsStore } from '@/libs/tenant-device-subscriptions/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import webPushNotifications from '@/libs/core/web-push-notifications';
import { authStore } from '@/libs/auth/+store/store';
import { AuthAction, AuthGetter } from '@/libs/auth/models/auth-state';
import NotificationScopeSelect from '@/libs/common/components-smart/NotificationScopeSelect.vue';
import { NotificationScope } from '@/libs/common/model/NotificationScope';
import { locationsStore } from '@/libs/locations/+state/store';
import { stationsStore } from '@/libs/stations/+state/store';
import { workplacesStore } from '@/libs/workplaces/+state/store';

@Component({
    components: { NotificationScopeSelect, KeyInput, Numpad },
})
export default class NotificationSubscription extends Page {
    protected dialog = false;
    protected hasValidRegistration = false;
    protected hasPushPermissions = false;
    protected hasValidSubscription = false;
    protected hasErrors = false;
    protected subscribing = false;
    protected reactivating = false;
    protected unsubscribing = false;
    protected notificationScope: NotificationScope | null = null;
    protected tenantUser = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;

    @Ref() notificationScopeSelect!: NotificationScopeSelect;

    @Watch('dialog')
    dialogChanged(v: boolean, p: boolean) {
        if (v && v != p) {
            this.refresh();
        }
    }

    get subscribedLocations(): LocationModel[] {
        const activeLocations = this.notificationScope?.locations ?? [];
        if (activeLocations.length > 0) {
            const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
            return locations.filter((x) => activeLocations.contains(x.id!));
        }
        return [];
    }

    get subscribedWorkplaces(): WorkplaceModel[] {
        const activeWorkplaces = this.notificationScope?.workplaces ?? [];
        if (activeWorkplaces.length > 0) {
            const workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data) ?? [];
            return workplaces.filter((x) => activeWorkplaces.contains(x.id!));
        }
        return [];
    }

    get subscribedStations(): StationModel[] {
        const activeStations = this.notificationScope?.stations ?? [];
        if (activeStations.length > 0) {
            const stations: StationModel[] = stationsStore.useGetter(CrudGetter.Data) ?? [];
            return stations.filter((x) => activeStations.contains(x.id!));
        }
        return [];
    }

    mounted() {
        this.getSubscription();
        this.refresh();
    }

    private refresh() {
        this.notificationScope = null;
        if (window.localStorage.NotificationScope) {
            this.notificationScope = JSON.parse(window.localStorage.NotificationScope) as NotificationScope;
            /*if (this.notificationScope) {
                this.$nextTick(() => {
                    this.notificationScopeSelect.setScope(this.notificationScope ?? {
                        locations: [],
                        workplaces: [],
                        stations: [],
                    });
                });
            }*/
        }
    }

    get isNotificationMode(): boolean {
        return authStore.useGetter(AuthGetter.GetNotificationMode);
    }

    get isLoading(): boolean {
        return tenantDeviceSubscriptionsStore.useGetter(CrudGetter.Pending, 'NotificationSubscriptionStatus');
    }

    get subscription(): TenantDeviceSubscriptionModel | null {
        const items: TenantDeviceSubscriptionModel[] =
            tenantDeviceSubscriptionsStore.useGetter(CrudGetter.Data, 'NotificationSubscriptionStatus') ?? [];
        return items.length > 0 ? items[0] : null;
    }

    async getFingerprint(): Promise<string> {
        const fingerprint = await getBrowserFingerprint();
        return btoa(fingerprint.toString());
    }

    protected onOpenDialogClick() {
        this.dialog = true;
    }

    protected onCloseClick() {
        this.dialog = false;
    }

    private async getSubscription(): Promise<void> {
        this.hasErrors = false;
        tenantDeviceSubscriptionsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<TenantDeviceSubscriptionModel>(
                undefined,
                [
                    {
                        field: 'tenantDevice.deviceId',
                        op: QueryOperator.Eq,
                        comparand: await this.getFingerprint(),
                    },
                ],
                undefined,
                false,
                'NotificationSubscriptionStatus'
            )
        );
        const subscriptions: TenantDeviceSubscriptionModel[] = await this.subscribe(
            tenantDeviceSubscriptionsStore,
            CrudReponse.GetAll,
            'NotificationSubscriptionStatus'
        );
        if (subscriptions.length > 0) {
            await this.checkNotificationStatus(subscriptions[0]);
        }
    }

    protected async onReactivateClick() {
        this.reactivating = true;
        try {
            await this.onSubscribeClick(this.notificationScope);
        } catch (e) {
            console.error(e);
        } finally {
            this.reactivating = false;
        }
    }

    public async onUnsubscribeClick() {
        this.unsubscribing = true;
        try {
            if (this.subscription) {
                const subscription = { ...this.subscription };
                subscription.subscription = null;
                subscription.isUnavailable = false;
                subscription.unavailabilityCount = 0;
                subscription.unavailableSince = null;
                subscription.tenantUserId = null;
                tenantDeviceSubscriptionsStore.dispatch(CrudAction.Update, {
                    item: subscription,
                    key: 'NotificationSubscriptionStatus',
                });
                await this.subscribe(tenantDeviceSubscriptionsStore, CrudReponse.Update, 'NotificationSubscriptionStatus');
                await this.getSubscription();
            }
            authStore.dispatch(AuthAction.SetNotificationMode, false);
            await webPushNotifications.postMessage({
                type: 'DeleteNotificationScope'
            });
            this.onCloseClick();
        } catch (e) {
            console.error(e);
        } finally {
            this.unsubscribing = false;
        }
    }

    protected async onSubscribeClick(scope: NotificationScope | null = null) {
        this.subscribing = true;
        try {
            const notificationScope = scope ?? this.notificationScopeSelect.getScope();
            const subscription: PushSubscription | null = await webPushNotifications.register();
            if (!subscription) {
                await this.checkNotificationStatus(null);
                this.hasErrors = true;
            } else {
                if (!this.subscription) {
                    tenantDeviceSubscriptionsStore.dispatch(CrudAction.Create, {
                        item: {
                            tenantDevice: {
                                deviceId: await this.getFingerprint(),
                            },
                            subscription: JSON.stringify(subscription),
                        } as TenantDeviceSubscriptionModel,
                        key: 'NotificationSubscriptionStatus',
                    });
                    await this.subscribe(tenantDeviceSubscriptionsStore, CrudReponse.Create, 'NotificationSubscriptionStatus');
                } else {
                    const subscriptionItem = { ...this.subscription };
                    subscriptionItem.subscription = JSON.stringify(subscription);
                    subscriptionItem.isUnavailable = false;
                    subscriptionItem.unavailabilityCount = 0;
                    subscriptionItem.unavailableSince = null;
                    subscriptionItem.tenantUserId = this.tenantUser.id!;
                    tenantDeviceSubscriptionsStore.dispatch(CrudAction.Update, {
                        item: subscriptionItem,
                        key: 'NotificationSubscriptionStatus',
                    });
                    await this.subscribe(tenantDeviceSubscriptionsStore, CrudReponse.Update, 'NotificationSubscriptionStatus');
                }
                await this.getSubscription();
                await webPushNotifications.postMessage({
                    type: 'SetNotificationScope',
                    scope: notificationScope,
                });
                window.localStorage.NotificationScope = JSON.stringify(notificationScope);
                authStore.dispatch(AuthAction.SetNotificationMode, true);
                this.onCloseClick();
            }
        } catch (e) {
            console.error(e);
        } finally {
            this.subscribing = false;
        }
    }

    private async checkNotificationStatus(tenantSubscription: TenantDeviceSubscriptionModel | null) {
        const hasValidRegistration = await webPushNotifications.hasValidRegistration();
        const hasPushPermissions = await webPushNotifications.hasPermission();
        if (!hasPushPermissions) {
            await webPushNotifications.unregister();
        }
        const subscription = await webPushNotifications.getSubscription();
        const storedSubsription: PushSubscription | null = tenantSubscription?.subscription
            ? JSON.parse(tenantSubscription!.subscription)
            : null;
        this.hasValidRegistration = hasValidRegistration;
        this.hasPushPermissions = hasPushPermissions;
        this.hasValidSubscription = subscription != null && storedSubsription?.endpoint == subscription?.endpoint;
        if (!hasValidRegistration || !hasPushPermissions) {
            this.hasErrors = true;
        }
    }
}
